import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Copy,
  FileText,
  LoaderCircle,
  RefreshCw,
  Search,
  ServerCrash,
  ShieldCheck,
  X,
} from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { ApiError } from '../../api/client'
import { getInstrument, getQuote, listInstruments } from './api'
import { stockKeys } from './queryKeys'
import { formatMagnitude } from './formatMagnitude'
import { useDebouncedValue } from './hooks/useDebouncedValue'
import type { CatalogState, Instrument, InstrumentStatus, Market, Quote, QuoteQuality } from './types'
import './stocks.css'

const MARKET_LABEL: Record<Market, string> = { CN: 'A 股', US: '美股' }
const STATUS_LABEL: Record<InstrumentStatus, string> = { ACTIVE: '交易中', SUSPENDED: '停牌', DELISTED: '已退市' }

function validMarket(value: string | null): Market {
  return value === 'US' ? 'US' : 'CN'
}

function validSymbol(value: string | null, market: Market): string {
  if (!value) return ''
  const symbol = value.trim().toUpperCase()
  return market === 'CN' ? (/^\d{6}$/.test(symbol) ? symbol : '') : (/^[A-Z0-9][A-Z0-9.$-]{0,9}$/.test(symbol) ? symbol : '')
}

function positiveInt(value: string | null): number {
  const page = Number(value)
  return Number.isInteger(page) && page > 0 ? page : 1
}

function useStockUrl() {
  const [params, setParams] = useSearchParams()
  const market = validMarket(params.get('market'))
  const rawSymbol = params.get('symbol')
  const symbol = validSymbol(rawSymbol, market)
  const page = positiveInt(params.get('page'))
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    if (rawSymbol && !symbol) setNotice('链接中的股票代码无效，已清除该参数。')
  }, [rawSymbol, symbol])

  useEffect(() => {
    const next = new URLSearchParams()
    next.set('market', market)
    if (symbol) next.set('symbol', symbol)
    if (page > 1) next.set('page', String(page))
    const current = params.toString()
    if (next.toString() !== current) setParams(next, { replace: true })
  }, [market, page, params, setParams, symbol])

  const update = (changes: Partial<{ market: Market; symbol: string; page: number }>) => {
    setNotice(null)
    const next = new URLSearchParams(params)
    const nextMarket = changes.market ?? market
    next.set('market', nextMarket)
    if (changes.market && changes.market !== market) next.delete('symbol')
    if ('symbol' in changes) {
      const nextSymbol = validSymbol(changes.symbol ?? '', nextMarket)
      if (nextSymbol) next.set('symbol', nextSymbol)
      else next.delete('symbol')
    }
    if ('page' in changes) {
      if ((changes.page ?? 1) > 1) next.set('page', String(changes.page))
      else next.delete('page')
    }
    setParams(next)
  }
  return { market, symbol, page, notice, update }
}

export function StocksPage() {
  const url = useStockUrl()
  const [searchText, setSearchText] = useState('')
  const [status, setStatus] = useState<InstrumentStatus | 'ALL'>('ALL')
  const modalRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const debouncedSearch = useDebouncedValue(searchText.trim(), 250)

  useEffect(() => {
    if (!url.symbol) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeButtonRef.current?.focus()
    return () => { document.body.style.overflow = previousOverflow }
  }, [url.symbol])

  const filterPending = searchText.trim() !== debouncedSearch
  const directoryQuery = useQuery({
    queryKey: stockKeys.instruments(url.market, url.page, status, debouncedSearch),
    queryFn: ({ signal }) => listInstruments(url.market, url.page, status, debouncedSearch, signal),
    staleTime: 300_000,
    gcTime: 1_800_000,
  })
  const instrumentQuery = useQuery({
    queryKey: stockKeys.instrument(url.market, url.symbol),
    queryFn: ({ signal }) => getInstrument(url.market, url.symbol, signal),
    enabled: Boolean(url.symbol),
    staleTime: 300_000,
    gcTime: 1_800_000,
  })
  const quoteQuery = useQuery({
    queryKey: stockKeys.quote(url.market, url.symbol),
    queryFn: ({ signal }) => getQuote(url.market, url.symbol, signal),
    enabled: Boolean(url.symbol),
    staleTime: 15_000,
    gcTime: 120_000,
  })

  const detailInstrument = instrumentQuery.data ?? directoryQuery.data?.items.find((item) => item.symbol === url.symbol)
  const chooseStock = (item: Instrument, trigger: HTMLButtonElement) => {
    triggerRef.current = trigger
    url.update({ market: item.market, symbol: item.symbol })
  }
  const closeDetail = () => {
    url.update({ symbol: '' })
    requestAnimationFrame(() => (triggerRef.current ?? searchInputRef.current)?.focus())
  }
  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      closeDetail()
      return
    }
    if (event.key !== 'Tab') return
    const focusable = Array.from(modalRef.current?.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ) ?? [])
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (!first || !last) return
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }
  const changeSearch = (value: string) => {
    setSearchText(value)
    if (url.page !== 1) url.update({ page: 1 })
  }

  return (
    <div className="terminal-shell">
      <header className="topbar">
        <Link className="brand" to="/stocks" aria-label="返回股票查询">
          <span className="brand-icon"><BarChart3 size={19} aria-hidden="true" /></span>
          <span><strong>个股查询</strong><small>A股 · 美股</small></span>
        </Link>
        <div className="topbar-meta">
          <nav className="site-nav" aria-label="主导航">
            <Link className="site-nav-link active" to="/stocks" aria-label="股票查询" aria-current="page"><BarChart3 size={16} aria-hidden="true" /><span className="nav-link-label">股票查询</span></Link>
            <Link className="site-nav-link" to="/api-docs" aria-label="API 文档"><FileText size={16} aria-hidden="true" /><span className="nav-link-label">API 文档</span></Link>
            <Link className="site-nav-link" to="/admin" aria-label="管理后台"><ShieldCheck size={16} aria-hidden="true" /><span className="nav-link-label">管理后台</span></Link>
          </nav>
          <span className="topbar-date">{new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium' }).format(new Date())}</span>
        </div>
      </header>

      <main className="workspace">
        <section className="query-panel" aria-label="股票查询工具">
          <div className="workspace-heading">
            <div><h1>股票查询</h1><p className="workspace-description">选择市场，按证券代码或名称查找。</p></div>
            <span className="system-status">只读数据服务</span>
          </div>
          <div className="query-controls">
            <div className="market-switch" role="group" aria-label="选择市场">
              {(['CN', 'US'] as Market[]).map((market) => (
                <button key={market} type="button" aria-pressed={url.market === market} className={url.market === market ? 'market-option active' : 'market-option'} onClick={() => { setSearchText(''); setStatus('ALL'); url.update({ market, page: 1 }) }}>
                  <span>{market}</span><small>{MARKET_LABEL[market]}</small>
                </button>
              ))}
            </div>
            {url.notice && <div className="url-notice" role="status"><AlertTriangle size={15} />{url.notice}</div>}
          </div>

          <form className="search-form" role="search" aria-label="筛选证券目录" onSubmit={(event) => event.preventDefault()}>
            <label className="field-label" htmlFor="stock-search">按代码或名称筛选目录</label>
            <div className="search-input-wrap">
              <Search size={17} aria-hidden="true" />
              <input ref={searchInputRef} id="stock-search" type="search" value={searchText} maxLength={64}
                onChange={(event) => changeSearch(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Escape') changeSearch('') }}
                placeholder="输入代码或名称" />
              {searchText && <button type="button" className="icon-button clear-button" onClick={() => changeSearch('')} aria-label="清除搜索"><X size={16} /></button>}
            </div>
          </form>
          <InstrumentDirectory
            query={directoryQuery}
            status={status}
            setStatus={(nextStatus) => { setStatus(nextStatus); url.update({ page: 1 }) }}
            onChoose={chooseStock}
            onPage={(page) => url.update({ page })}
            filtering={filterPending}
            searchTerm={debouncedSearch}
            selectedSymbol={url.symbol}
          />
        </section>

      </main>
      {url.symbol && (
        <div className="modal-backdrop" onClick={(event) => { if (event.target === event.currentTarget) closeDetail() }}>
          <div ref={modalRef} className="detail-area modal-card" role="dialog" aria-modal="true"
            aria-label={`${url.symbol} 股票详情`} onKeyDown={onDialogKeyDown}>
            <div className="modal-toolbar">
              <span className="modal-kicker">{MARKET_LABEL[url.market]} · 股票详情</span>
              <button ref={closeButtonRef} type="button" className="icon-button modal-close"
                aria-label="关闭股票详情" onClick={closeDetail}><X size={20} /></button>
            </div>
            {detailInstrument ? (
              <StockDetail instrument={detailInstrument} quoteQuery={quoteQuery} onRetry={() => void quoteQuery.refetch()} />
            ) : instrumentQuery.isPending ? (
              <DetailSkeleton />
            ) : instrumentQuery.isError ? (
              <ErrorState error={instrumentQuery.error} onRetry={() => void instrumentQuery.refetch()} />
            ) : null}
          </div>
        </div>
      )}
      <footer className="page-footer"><span>个股查询 · 只读数据服务</span><span>数据仅供查询，不构成投资建议</span></footer>
    </div>
  )
}

function InstrumentDirectory({
  query, status, setStatus, onChoose, onPage, filtering, searchTerm, selectedSymbol,
}: {
  query: ReturnType<typeof useQuery<import('./types').InstrumentsData, Error>>
  status: InstrumentStatus | 'ALL'
  setStatus: (status: InstrumentStatus | 'ALL') => void
  onChoose: (item: Instrument, trigger: HTMLButtonElement) => void
  onPage: (page: number) => void
  filtering: boolean
  searchTerm: string
  selectedSymbol: string
}) {
  const data = filtering ? undefined : query.data
  const loading = filtering || query.isPending
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1
  return (
    <div className="directory-panel" aria-busy={filtering || query.isFetching}>
      <div className="directory-toolbar">
        <div><p className="field-label">证券目录 {searchTerm && '/ 搜索筛选'}</p><span aria-live="polite">{data ? `${data.total.toLocaleString()} 个证券` : '加载证券目录'}</span></div>
        <select aria-label="证券状态筛选" value={status} onChange={(event) => setStatus(event.target.value as InstrumentStatus | 'ALL')}>
          <option value="ALL">全部状态</option><option value="ACTIVE">交易中</option><option value="SUSPENDED">停牌</option><option value="DELISTED">已退市</option>
        </select>
      </div>
      {data?.catalog && <CatalogNotice catalog={data.catalog} />}
      {query.isFetching && !query.isPending && !filtering && <div className="directory-progress" role="status"><LoaderCircle className="spin" size={15} />正在更新目录</div>}
      {loading && <DirectorySkeleton />}
      {!filtering && query.isError && <ErrorState error={query.error} onRetry={() => void query.refetch()} />}
      {!loading && !query.isError && data?.items.length === 0 && <div className="directory-empty"><CircleHelp size={18} />当前筛选没有证券</div>}
      <div className="directory-list">
        {data?.items.map((item) => <button type="button" className={item.symbol === selectedSymbol ? "directory-row selected" : "directory-row"} aria-pressed={item.symbol === selectedSymbol} key={item.instrument_id} onClick={(event) => onChoose(item, event.currentTarget)}>
          <span><strong>{item.symbol}</strong><small>{item.name}</small></span><span className="directory-exchange">{item.exchange}</span><span className={`status-tag status-${item.status.toLowerCase()}`}>{STATUS_LABEL[item.status]}</span>
        </button>)}
      </div>
      <div className="pagination"><button className="icon-button" aria-label="上一页" disabled={!data || data.page <= 1} onClick={() => onPage((data?.page ?? 1) - 1)}><ChevronLeft size={17} /></button><span>{data?.page ?? 1} / {totalPages}</span><button className="icon-button" aria-label="下一页" disabled={!data || data.page >= totalPages} onClick={() => onPage((data?.page ?? 1) + 1)}><ChevronRight size={17} /></button></div>
    </div>
  )
}

function CatalogNotice({ catalog }: { catalog: CatalogState }) {
  const lastSyncedAt = catalog.last_synced_at
  const lastSyncedDate = lastSyncedAt ? new Date(lastSyncedAt) : null
  const hasUpdateDate = lastSyncedDate !== null && !Number.isNaN(lastSyncedDate.getTime())
  return <div className={`catalog-notice ${!catalog.ready ? 'warning' : ''}`}>
    {!catalog.ready && <span>目录尚未就绪</span>}
    {hasUpdateDate ? <><span>{!catalog.ready ? '· ' : ''}上次数据更新</span><time dateTime={lastSyncedAt!}>{new Intl.DateTimeFormat('zh-CN', { dateStyle: 'medium' }).format(lastSyncedDate)}</time></> : catalog.ready ? <span>上次数据更新日期未知</span> : null}
  </div>
}

function DetailSkeleton() {
  return <div className="detail-content"><div className="skeleton skeleton-kicker" /><div className="skeleton skeleton-title" /><div className="skeleton skeleton-price" /><div className="detail-grid"><div className="skeleton skeleton-box" /><div className="skeleton skeleton-box" /><div className="skeleton skeleton-box" /><div className="skeleton skeleton-box" /></div></div>
}

function StockDetail({ instrument, quoteQuery, onRetry }: { instrument: Instrument; quoteQuery: ReturnType<typeof useQuery<{ quote: Quote }, Error>>; onRetry: () => void }) {
  const quote = quoteQuery.data?.quote
  const command = `/购买股票 ${instrument.market === 'CN' ? 'A' : 'US'} ${instrument.symbol}`
  return <div className="detail-content">
    <div className="detail-topline"><div><div className="instrument-title"><h2>{instrument.name}</h2><code>{instrument.symbol}</code></div><p className="instrument-meta">{instrument.exchange} · {instrument.currency} · {STATUS_LABEL[instrument.status]}</p></div><button className="button button-quiet" onClick={onRetry}><RefreshCw size={16} className={quoteQuery.isFetching ? 'spin' : ''} />读取最新缓存</button></div>
    {quoteQuery.isError ? <ErrorState error={quoteQuery.error} onRetry={onRetry} /> : quoteQuery.isPending ? <QuoteSkeleton /> : quote ? <QuoteSummary quote={quote} instrument={instrument} command={command} /> : <ErrorState error={new Error('行情暂时不可用')} onRetry={onRetry} />}
    <div className="instrument-sheet"><div className="sheet-heading"><span>证券档案</span></div><div className="sheet-grid"><Definition label="证券代码" value={instrument.symbol} mono /><Definition label="交易所" value={instrument.exchange} /><Definition label="交易单位" value={instrument.lot_size} /><Definition label="最小变动" value={instrument.tick_size} /><Definition label="可碎股" value={instrument.fractional_allowed ? '支持' : '不支持'} /><Definition label="目录状态" value={STATUS_LABEL[instrument.status]} /></div></div>
  </div>
}

function QuoteSummary({ quote, instrument, command }: { quote: Quote; instrument: Instrument; command: string }) {
  const quality = quote.freshness.quality
  const change = quote.change ? Number(quote.change) : null
  const direction = change === null ? 'flat' : change > 0 ? 'rise' : change < 0 ? 'fall' : 'flat'
  return <>
    <QuoteQualityNotice quote={quote} />
    <section className="quote-hero"><div className="price-column"><span className="quote-label">最新价 · {quote.currency}</span><strong className={quality === 'INVALID' || quality === 'UNAVAILABLE' ? 'price unavailable' : 'price'}>{quality === 'INVALID' || quality === 'UNAVAILABLE' ? '--' : quote.last_price ?? '--'}</strong><span className={`change ${direction}`}>{direction === 'rise' && <ArrowUpRight size={18} />}{direction === 'fall' && <ArrowDownRight size={18} />}{quote.change ?? '--'} {quote.change_percent !== null ? `(${quote.change_percent}%)` : ''} <small>{direction === 'rise' ? '上涨' : direction === 'fall' ? '下跌' : '暂无变化'}</small></span></div><div className="quote-context"><div><span>市场状态</span><strong>{quote.market_status === 'OPEN' ? '交易中' : quote.market_status === 'CLOSED' ? '已收市' : '状态未知'}</strong></div><div><span>来源时间</span><strong>{quote.freshness.source_timestamp ? formatTime(quote.freshness.source_timestamp) : '--'}</strong></div><div><span>抓取时间</span><strong>{formatTime(quote.freshness.fetched_at)}</strong></div><div><span>行情来源</span><strong>{quote.freshness.source ?? '--'}</strong></div></div></section>
    <section className="stats-section"><div className="section-heading"><span>关键指标</span><small>{instrument.market === 'US' && quote.freshness.is_delayed ? '延迟行情' : '最新缓存快照'}</small></div><div className="stats-grid"><Definition label="今开" value={quote.open} /><Definition label="最高" value={quote.high} /><Definition label="最低" value={quote.low} /><Definition label="昨收" value={quote.previous_close} /><MagnitudeDefinition label="成交量" value={quote.volume} /><MagnitudeDefinition label="成交额" value={quote.amount} /></div></section>
    <section className="copy-section"><span className="section-heading-text">快速复制</span><div className="copy-actions"><CopyAction label="复制代码" value={instrument.symbol} /><CopyAction label="复制名称" value={instrument.name} /><CopyAction label="复制微信指令" value={command} wide /></div></section>
  </>
}

function QuoteQualityNotice({ quote }: { quote: Quote }) {
  const quality = quote.freshness.quality
  const text: Record<QuoteQuality, string> = { VALID: quote.freshness.is_delayed ? `延迟行情 · 供应商约 ${quote.freshness.provider_delay_minutes ?? '--'} 分钟延迟` : '行情有效', STALE: '显示上一次缓存行情，等待后台更新', INVALID: '行情数据无效，当前价格已隐藏', UNVERIFIED_TIME: '价格可用，但来源时间未验证', UNAVAILABLE: '尚无可用缓存行情，后台同步后将自动显示' }
  const icon = quality === 'VALID' ? <Check size={16} /> : <AlertTriangle size={16} />
  return <div className={`quality-notice quality-${quality.toLowerCase()}`}>{icon}<span>{text[quality]}</span>{quote.freshness.reason && <small>{quote.freshness.reason}</small>}</div>
}

function QuoteSkeleton() { return <div className="quote-skeleton"><div className="skeleton skeleton-price" /><div className="detail-grid"><div className="skeleton skeleton-box" /><div className="skeleton skeleton-box" /><div className="skeleton skeleton-box" /></div></div> }

function Definition({ label, value, mono = false, title }: { label: string; value: string | null; mono?: boolean; title?: string | null }) { return <div className="definition"><span>{label}</span><strong className={mono ? 'mono' : ''} title={title ?? undefined}>{value ?? '--'}</strong></div> }

function MagnitudeDefinition({ label, value }: { label: string; value: string | null }) {
  const formatted = formatMagnitude(value)
  const unit = formatted.match(/(?:万亿|千|万|亿)$/)?.[0] ?? ''
  const numeric = unit ? formatted.slice(0, -unit.length) : formatted
  return <div className="definition"><span>{label}</span><strong title={value ?? undefined}>{numeric}{unit && <span className="metric-unit">{unit}</span>}</strong></div>
}

function CopyAction({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  const [copied, setCopied] = useState(false)
  const [fallback, setFallback] = useState(false)
  const fallbackRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!fallback) return
    fallbackRef.current?.focus()
    fallbackRef.current?.select()
  }, [fallback])
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard-unavailable')
      await navigator.clipboard.writeText(value)
      setCopied(true); setFallback(false)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      setFallback(true)
    }
  }
  return <div className={`copy-control ${wide ? 'wide' : ''} ${fallback ? 'has-fallback' : ''}`}><button type="button" className="copy-button" onClick={copy}><span>{copied ? <Check size={15} /> : <Copy size={15} />}</span>{copied ? '已复制' : label}</button>{fallback && <input ref={fallbackRef} className="copy-fallback" value={value} readOnly aria-label={`${label}文本`} onFocus={(event) => event.currentTarget.select()} />}</div>
}

function ErrorState({ error, onRetry, compact = false }: { error: unknown; onRetry: () => void; compact?: boolean }) {
  const apiError = error instanceof ApiError ? error : null
  const rateLimited = apiError?.status === 429
  const [remaining, setRemaining] = useState(rateLimited ? Math.max(0, apiError?.retryAfterSeconds ?? 0) : 0)
  useEffect(() => {
    setRemaining(rateLimited ? Math.max(0, apiError?.retryAfterSeconds ?? 0) : 0)
  }, [apiError?.retryAfterSeconds, rateLimited])
  useEffect(() => {
    if (remaining <= 0) return
    const timer = window.setTimeout(() => setRemaining((seconds) => Math.max(0, seconds - 1)), 1_000)
    return () => window.clearTimeout(timer)
  }, [remaining])
  const message = rateLimited ? `请求过于频繁${remaining > 0 ? `，请 ${remaining} 秒后重试` : ''}` : apiError?.code === 'CATALOG_NOT_READY' ? '证券目录尚未就绪，请稍后重试。可在管理后台查看证券目录更新记录与自动更新配置。' : apiError?.message ?? '请求失败，请稍后重试。'
  return <div className={`error-state ${compact ? 'compact' : ''}`}><ServerCrash size={compact ? 16 : 19} /><span>{message}{apiError?.requestId && <small>request_id: {apiError.requestId}</small>}</span><button type="button" className="text-button" onClick={onRetry} disabled={rateLimited && remaining > 0}>{rateLimited && remaining > 0 ? `${remaining}s` : '重试'}</button></div>
}

function DirectorySkeleton() { return <div className="directory-skeleton">{[1, 2, 3, 4, 5].map((item) => <div key={item}><span className="skeleton" /><span className="skeleton" /><span className="skeleton" /></div>)}</div> }

function formatTime(value: string | null) {
  if (!value) return '--'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '时间未知'
  return new Intl.DateTimeFormat('zh-CN', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}
